import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface BlogPost {
  slug: string;
  title: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  content: string[];
}

const posts: BlogPost[] = [
  {
    slug: 'reasons-airbnb-not-getting-booked',
    title: "5 Reasons Your Airbnb Listing Isn't Getting Booked (And How to Fix It)",
    category: 'Listing Optimization',
    date: 'June 10, 2025',
    readTime: '5 min read',
    image:
      'https://public.readdy.ai/ai/img_res/0833f20065a67439976be00fc6960777.jpg',
    content: [
      "Most hosts blame slow seasons or bad luck when bookings dry up. The truth? Your listing is likely making avoidable mistakes that push guests straight to your competitors. After auditing hundreds of listings and managing 15+ properties ourselves, we've identified the five most common culprits — and exactly how to fix them.",
      "**1. Your Photos Are Letting You Down**\n\nGuests make booking decisions in seconds, and photos are the first thing they see. Dark, blurry, or poorly composed images signal a low-quality stay before a guest even reads your description. Professional photography isn't a luxury — it's a necessity. Invest in a photographer who specializes in interiors, shoot during the day with natural light, and make sure every room is staged and spotless. Your cover photo should be your most impressive shot, ideally the living room or a standout feature of the property.",
      "**2. Your Title and Description Aren't Working Hard Enough**\n\nAirbnb's search algorithm rewards listings that use relevant keywords. If your title is something generic like 'Cozy 2-Bedroom Apartment,' you're invisible. A strong title highlights your property's best feature, its location advantage, and a key amenity — all in under 50 characters. Your description should answer every question a guest might have before they ask it: what's nearby, what's included, what makes your place special. Write for the guest, not for yourself.",
      "**3. Your Pricing Is Off**\n\nPricing too high drives guests away. Pricing too low attracts the wrong guests and leaves money on the table. Static pricing — setting one rate and leaving it — is one of the biggest revenue mistakes hosts make. Dynamic pricing tools like PriceLabs or Wheelhouse adjust your rates based on local demand, seasonality, events, and competitor pricing. If you're not using dynamic pricing, you're almost certainly underperforming.",
      "**4. You Have Too Few or Too Negative Reviews**\n\nNew listings with zero reviews struggle to compete against established ones. And listings with unresolved negative reviews lose bookings fast. If you're new, consider offering a slight discount for your first few bookings to build your review base quickly. For existing hosts, proactively ask guests to leave a review after checkout — a simple, friendly message goes a long way. Always respond to negative reviews professionally and constructively. Future guests read your responses.",
      "**5. Your Amenities Don't Match Guest Expectations**\n\nGuests filter by amenities. If you're missing essentials like fast WiFi, a fully equipped kitchen, or a dedicated workspace, you're being filtered out of searches before guests even see your listing. Review what your competitors are offering and identify gaps. Small upgrades — a coffee machine, blackout curtains, a welcome basket — can dramatically improve your listing's appeal and review scores.",
      "**The Bottom Line**\n\nNone of these problems are permanent. Every one of them is fixable with the right strategy and attention to detail. If you're not sure where your listing is falling short, a professional performance audit can identify exactly what's holding you back and give you a clear action plan.",
    ],
  },
  {
    slug: 'empty-property-to-5-star-listing',
    title: 'From Empty Property to 5-Star Listing: Our End-to-End Setup Process',
    category: 'Setup & Launch',
    date: 'May 28, 2025',
    readTime: '6 min read',
    image:
      'https://readdy.ai/api/search-image?query=beautiful-airbnb-property-transformation-before-and-after-professional-interior-design-staging-and-setup-modern-furnished-short-term-rental-ready-for-guests-with-elegant-decor-and-welcoming-atmosphere&width=1200&height=600&seq=post-hero-2&orientation=landscape',
    content: [
      "We've launched dozens of listings from scratch — bare walls, empty rooms, zero reviews. Every single one followed the same proven process. Here's exactly how we take an empty property and turn it into a fully booked, 5-star Airbnb.",
      "**Step 1: Market Research and Positioning**\n\nBefore we touch a single piece of furniture, we study the market. We analyze comparable listings in the area, identify what's performing well, what's underperforming, and where the gaps are. We look at average daily rates, occupancy trends, and guest demographics. This research shapes every decision that follows — from furnishing style to pricing strategy to the amenities we prioritize.",
      "**Step 2: Furnishing and Interior Design**\n\nWe furnish with the guest experience in mind, not personal taste. That means durable, photogenic furniture in neutral tones that photograph well and appeal to a broad audience. We focus on the spaces guests care most about: the bedroom (comfort and blackout curtains), the living area (seating and entertainment), and the kitchen (fully equipped and organized). Every item serves a purpose. Clutter is the enemy of 5-star reviews.",
      "**Step 3: Professional Staging and Photography**\n\nOnce the property is furnished, we stage it for photography. This means fresh flowers, perfectly made beds, clean countertops, and strategic lighting. We work with photographers who specialize in short-term rental properties — people who know how to make a space look inviting and spacious. Great photos are the single highest-ROI investment in your listing.",
      "**Step 4: Listing Creation and Optimization**\n\nWith photos in hand, we write the listing. The title is crafted to rank in Airbnb search and immediately communicate the property's value. The description is detailed, honest, and written from the guest's perspective. We set up house rules, check-in instructions, and a welcome guide. We configure the listing on Airbnb, VRBO, and Booking.com for maximum visibility.",
      "**Step 5: Pricing Strategy and Launch**\n\nWe set up dynamic pricing from day one. For new listings, we use a strategic launch pricing approach — slightly below market rate to generate early bookings and reviews quickly, then adjust upward as the listing gains traction. We monitor performance daily in the first 30 days and make adjustments as needed.",
      "**Step 6: Systems and Operations**\n\nA great listing is only half the battle. We set up automated messaging sequences for every stage of the guest journey — booking confirmation, pre-arrival instructions, check-in day message, mid-stay check-in, and checkout reminder. We coordinate cleaning schedules, create a property manual, and establish protocols for maintenance issues. These systems ensure every guest gets the same excellent experience, regardless of who's managing the property on any given day.",
      "**The Result**\n\nWhen done right, this process consistently produces listings that achieve Superhost status within their first year, maintain occupancy rates above 75%, and generate 5-star reviews as the norm rather than the exception. The key is treating your Airbnb like a business from day one.",
    ],
  },
  {
    slug: 'hosting-on-hard-mode-lagos',
    title: 'Hosting on Hard Mode: What Running Airbnb in Lagos Taught Us About 5-Star Hospitality',
    category: 'Our Story',
    date: 'May 14, 2025',
    readTime: '7 min read',
    image:
      'https://readdy.ai/api/search-image?query=vibrant-lagos-nigeria-cityscape-at-sunset-with-modern-buildings-and-busy-streets-showing-dynamic-african-city-energy-professional-urban-photography-with-warm-golden-light-and-contemporary-architecture&width=1200&height=600&seq=post-hero-3&orientation=landscape',
    content: [
      "When we started hosting on Airbnb in Lagos, Nigeria in 2019, we didn't know we were signing up for a masterclass in hospitality under pressure. Lagos is one of the most operationally challenging cities in the world for short-term rentals. What we learned there shaped everything we do today.",
      "**The Lagos Challenge**\n\nLet's be direct about what hosting in Lagos means. Electricity is unreliable — power outages can last hours or days. Water supply is inconsistent. Internet connectivity can drop without warning. Traffic makes logistics unpredictable. These aren't occasional inconveniences; they're daily realities that every host in the city has to navigate.",
      "For most hosts, these challenges become excuses for mediocre guest experiences. For us, they became the forcing function that made us build better systems than anyone else.",
      "**Building Systems for Every Scenario**\n\nWe couldn't control the infrastructure. But we could control our response to it. We invested in backup power systems so guests never experienced a blackout. We installed water storage solutions so supply interruptions were invisible to guests. We set up redundant internet connections so WiFi stayed reliable. We built detailed contingency protocols for every scenario we could imagine — and some we couldn't until they happened.",
      "Every problem we encountered became a documented solution. Every guest complaint became a system improvement. Over time, we built an operational playbook that could handle almost anything Lagos threw at us.",
      "**The 4.95 Rating**\n\nDespite all of this — or perhaps because of it — we maintained a 4.95 Airbnb rating. Guests from around the world stayed at our properties and left glowing reviews. Not because Lagos was easy, but because we worked harder than the challenges we faced.",
      "That rating isn't just a number. It's proof that exceptional hospitality is a choice, not a circumstance. It's proof that the right systems can deliver a 5-star experience regardless of external conditions.",
      "**What This Means for Your Hosting**\n\nHere's the insight that changed how we think about hospitality: if you build your systems to handle the worst-case scenario, the average case becomes effortless. Most hosts build for the best case and scramble when things go wrong. We built for the worst case and consistently exceeded expectations.",
      "The systems we developed in Lagos — for guest communication, operational contingencies, quality control, and performance optimization — are the same systems we use today to manage 15+ listings and coach hosts around the world. They work in Lagos. They work in London. They work anywhere.",
      "**The Lesson**\n\nDon't wait for perfect conditions to build a great hosting business. The hosts who succeed long-term are the ones who build systems that work under pressure. If you're struggling with your listing — whether it's low occupancy, inconsistent reviews, or operational chaos — the answer isn't better luck. It's better systems.",
    ],
  },
];

const categoryColors: Record<string, string> = {
  'Listing Optimization': 'bg-amber-100 text-amber-900',
  'Setup & Launch': 'bg-emerald-100 text-emerald-900',
  'Our Story': 'bg-blue-100 text-blue-900',
};

function renderParagraph(text: string, index: number) {
  if (text.startsWith('**') && text.includes('\n\n')) {
    const [heading, ...rest] = text.split('\n\n');
    const headingText = heading.replace(/\*\*/g, '');
    return (
      <div key={index} className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3">{headingText}</h3>
        <p className="text-gray-600 leading-relaxed">{rest.join('\n\n')}</p>
      </div>
    );
  }
  if (text.startsWith('**') && text.endsWith('**')) {
    return (
      <h3 key={index} className="text-xl font-bold text-gray-900 mb-3 mt-6">
        {text.replace(/\*\*/g, '')}
      </h3>
    );
  }
  return (
    <p key={index} className="text-gray-600 leading-relaxed mb-6">
      {text}
    </p>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = posts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-40 pb-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-amber-900 font-medium hover:underline">
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const otherPosts = posts.filter((p) => p.slug !== slug);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Image */}
      <div className="relative h-[480px] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-4xl mx-auto">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 ${categoryColors[post.category]}`}>
            {post.category}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-white/70 text-sm">
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <Link
            to="/blog"
            className="inline-flex items-center text-amber-900 text-sm font-medium mb-10 hover:underline"
          >
            <i className="ri-arrow-left-line mr-1"></i>
            Back to Blog
          </Link>

          <div className="prose max-w-none">
            {post.content.map((paragraph, index) => renderParagraph(paragraph, index))}
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-br from-amber-900 to-amber-950 rounded-3xl p-10 text-center">
            <h3 className="text-2xl font-bold text-white mb-3">
              Ready to Apply These Strategies?
            </h3>
            <p className="text-white/80 mb-6">
              Let's talk about your property and build a plan that works for your specific situation.
            </p>
            <Link
              to="/get-started"
              className="inline-flex items-center px-8 py-3 bg-white text-amber-900 rounded-full text-sm font-medium hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started Today
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </article>

      {/* More Articles */}
      {otherPosts.length > 0 && (
        <section className="py-16 bg-amber-50">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">More Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {otherPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl hover:border-amber-300 transition-all"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-3 ${categoryColors[p.category]}`}>
                      {p.category}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-amber-900 transition-colors leading-snug mb-2">
                      {p.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span>{p.date}</span>
                      <span>·</span>
                      <span>{p.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
